import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FamilyTask } from './useOptimizedTasks';
import { useAuth } from './useAuth';

interface TaskList {
  id: string;
  name: string;
  icon: string | null;
  family_id: string;
  created_by: string;
  event_id: string | null;
  display_order: number | null;
  created_at: string;
  updated_at: string;
  pending_count: number;
  completed_count: number;
}

interface TaskListMember {
  id: string;
  task_list_id: string;
  profile_id: string;
  profile: any;
}

interface TasksPageData {
  tasks: FamilyTask[];
  task_lists: TaskList[];
  task_list_members: TaskListMember[];
  persons: any[];
  profiles: any[];
}

/**
 * Hook optimisé qui charge toutes les données de la page tâches en une seule requête RPC
 */
export function useTasksPageData(familyId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Get user profile id from cache or fetch
  const profileQuery = useQuery({
    queryKey: ['user-profile-id', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
      return data?.id || null;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const profileId = profileQuery.data;

  return useQuery({
    queryKey: ['tasks-page-data', familyId, profileId],
    queryFn: async (): Promise<TasksPageData> => {
      if (!familyId) {
        return { tasks: [], task_lists: [], task_list_members: [], persons: [], profiles: [] };
      }

      const { data, error } = await supabase.rpc('get_tasks_page_data', {
        p_family_id: familyId,
        p_user_profile_id: profileId || '00000000-0000-0000-0000-000000000000'
      });

      if (error) {
        console.error('Error fetching tasks page data:', error);
        throw error;
      }

      const result = (data as unknown) as TasksPageData;

      // Pré-remplir les caches individuels
      if (result.tasks) {
        queryClient.setQueryData(['optimized-tasks', familyId], result.tasks);
      }
      if (result.task_lists) {
        queryClient.setQueryData(['task-lists', familyId], result.task_lists);
      }
      if (result.persons) {
        queryClient.setQueryData(['optimized-person-profiles', familyId], result.persons);
      }

      return result;
    },
    enabled: !!familyId && profileQuery.isSuccess,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
